import React, { useEffect, useState } from 'react';
import { ExternalLink, Newspaper, ShieldCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { api } from '../lib/api';
import { track } from '../lib/analytics';

function SkeletonCard() {
  return (
    <div className="rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
      <div className="aspect-[16/10] bg-gray-100" />
      <div className="p-5 space-y-3">
        <div className="h-3 w-24 bg-gray-100 rounded" />
        <div className="h-5 w-full bg-gray-100 rounded" />
        <div className="h-3 w-3/4 bg-gray-100 rounded" />
      </div>
    </div>
  );
}

export default function NewsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.listNews();
        if (!cancelled) setItems(res.items || []);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onClick = (a) => {
    try { track('press_click', { source: a.source, id: a.id }); } catch {}
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Prensa y noticias inmobiliarias · habita.co"
        description="Fuentes confiables del sector inmobiliario en Colombia: La República, Portafolio, El Tiempo, Semana, Camacol, DANE, BBVA Research y ArchDaily. Cada enlace verificado automáticamente."
      />
      <Header />

      <section className="bg-habita-cream py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-habita-mint text-habita rounded-full text-xs font-semibold tracking-wide">
            <Newspaper className="w-3.5 h-3.5" /> PRENSA · FUENTES CURADAS
          </span>
          <h1 className="font-serif text-5xl lg:text-6xl font-bold text-habita-dark mt-5 leading-[1.05]">
            El mercado inmobiliario, <span className="italic text-amber-600">de fuentes confiables</span>.
          </h1>
          <p className="text-gray-600 mt-5 text-lg max-w-2xl mx-auto">
            Seleccionamos las mejores secciones de medios y organismos oficiales para el sector vivienda en Colombia. Cada enlace se verifica automáticamente cada 12 horas.
          </p>
          <div className="inline-flex items-center gap-2 mt-6 text-xs text-gray-500">
            <ShieldCheck className="w-3.5 h-3.5 text-habita" />
            <span>Enlaces validados · Contenido actualizado por cada medio</span>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16">
        {error ? (
          <div className="text-center py-20">
            <p className="text-gray-500">No pudimos cargar las fuentes en este momento. Intenta de nuevo en unos minutos.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="news-grid">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
              : items.map((n) => (
                <a
                  key={n.id}
                  href={n.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onClick(n)}
                  data-testid={`news-card-${n.id}`}
                  className="group block rounded-2xl overflow-hidden border border-gray-100 hover-lift transition-all bg-white"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-gray-100 relative">
                    <img
                      src={n.image}
                      alt={n.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 bg-white/95 backdrop-blur-sm rounded-full text-[10px] font-bold uppercase tracking-widest text-habita">
                      <ShieldCheck className="w-3 h-3" /> Verificado
                    </span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="uppercase tracking-widest text-amber-600 font-semibold">{n.category}</span>
                      <span className="text-gray-300">·</span>
                      <span className="text-gray-500 font-medium">{n.source}</span>
                    </div>
                    <h3 className="font-serif text-xl font-bold text-habita-dark mt-2 group-hover:text-habita transition-colors line-clamp-2 leading-tight">
                      {n.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2 leading-relaxed">{n.excerpt}</p>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                      <span className="text-gray-400">{n.read}</span>
                      <span className="inline-flex items-center gap-1 text-habita font-semibold group-hover:gap-2 transition-all">
                        Leer en {n.source} <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </a>
              ))}
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-12 max-w-xl mx-auto">
          Los enlaces te llevan al contenido original de cada medio. habita.co no edita ni reproduce los artículos; solo recomendamos secciones activas de fuentes confiables del sector inmobiliario colombiano.
        </p>
      </div>

      <Footer />
    </div>
  );
}
